import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Button,
  FormControlLabel,
  Typography,
  Fab,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFetchTable } from "@hooks/useFetchDataTable";
import BackgroundImage from "@components/BackgroundImageCst";
import { MethodType } from "src/services/type";
import Table from "@components/Table";
import HeroCst from "@components/HeroCst";
import Breadcrumb from "@components/Breadcrumb";
import { useLocation, useParams } from "react-router-dom";
import { useFetchDetail } from "@hooks/useFetchDetail";
import {
  formatSessionOccupiedError,
  isSessionOverlapError,
  ServicesURL,
} from "@utils/utilsApi";
import { fetcher } from "src/services/config";
import { PagesID } from "@utils/utilsConfigurations";
import { User } from "src/interfaces/User";
import { ActionsType } from "src/interfaces/Common";
import useSWRMutation from "swr/mutation";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ButtonBackPage from "@components/ButtonBackPage";
import { LOType } from "@utils/utilsConfigurations";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import AnimationFadeIn from "@components/AnimationFadeIn";
import ButtonCst from "@components/ButtonCst";
import { useTheme } from "@mui/material";
import CustomRowAction from "@components/Table/components/CustomRowAction";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";
import ModalCst from "@components/ModalCst";
import dayjs from "dayjs";
import FormLoadingStateCst from "@components/FormLoadingStateCst";

const AssociationEditionUser: React.FC<{
  type: LOType;
  isToj?: boolean;
  isVirtual?: boolean;
}> = ({ type, isToj = false, isVirtual = false }) => {
  const theme = useTheme();
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message?: string;
    customContent?: JSX.Element;
  }>();
  const [openConfirm, setOpenConfirm] = useState(false);
  const { t } = useTranslation(["users-page", "modal"]);
  const { id, idLO, idEdition, idEditionLOActivity } = useParams();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["enrollment.edition.user"] + pathname}`;

  const { columns, dataTable, actionsTopToolbar, actionForRow, isLoading } =
    useFetchTable<User[]>(
      `${ServicesURL.getEnrollment}?corporateId=${id || "1"}&objectId=${idEditionLOActivity}&enrollType=TOP,TOP_MANDATORY&editionToEnroll=${idEdition}${isToj ? "&isToj=true" : ""}`,
      PagesID["enrollment.edition.user"],
      filters[filterId],
    );

  //CORPORATE DATA
  const { dataDetail } = useFetchDetail(
    ServicesURL.corporate,
    id || "1",
    PagesID.corporate,
  );

  type NotifyMode = "NONE" | "STARTED" | "ENDED";
  const [notifyMode, setNotifyMode] = useState<NotifyMode>("NONE");
  const [userAlreadySelected, setUserAlreadySelected] = useState<number[]>([]);
  const [selected, setSelected] = useState<any>({});
  const [openBatchStatus, setOpenBatchStatus] = useState(false);
  const [batchStatusMessage, setBatchStatusMessage] = useState("");
  const location = useLocation();

  const MAX_CAPACITY_ERROR_MESSAGE = "MAX_EDITION_CAPACITY_REACHED";

  const { trigger: triggerPatchAssociation } = useSWRMutation(
    `${ServicesURL.patchEnrollment}`,
    (url: string, { arg }: { arg: any[] }) =>
      fetcher(url, MethodType.PATCH, arg),
    { onError: () => {} },
  );

  const { dataDetail: dataEdition } = useFetchDetail(
    ServicesURL.getEditionsDetail + `${isVirtual ? "/virtual" : "/physical"}`,
    idEdition + `?corporateId=${id}` || "",
    PagesID["corporate.physical-editions"],
  );

  useEffect(() => {
    if (!dataEdition) {
      setNotifyMode("NONE");
      return;
    }
    const started =
      dataEdition.start_date && dayjs().isAfter(dayjs(dataEdition.start_date));
    const ended =
      dataEdition.end_date && dayjs().isAfter(dayjs(dataEdition.end_date));
    if (ended) {
      setNotifyMode("ENDED");
    } else if (started) {
      setNotifyMode("STARTED");
    } else {
      setNotifyMode("NONE");
    }
  }, [dataEdition]);

  const modalConfig = {
    STARTED: {
      title: t("association-user.notify-modal.started-title"),
      message: t("association-user.notify-modal.started-message"),
    },
    ENDED: {
      title: t("association-user.notify-modal.ended-title"),
      message: t("association-user.notify-modal.ended-message"),
    },
  };

  const modalText = notifyMode === "NONE" ? null : modalConfig[notifyMode];

  // const handleCloseToastMessage = () => {
  //   setOpenToast(undefined);
  // };

  // const getObjectId = () => {
  //   // If it's an Edition objectId is the learning_objects id of edition
  //   // If it's not an Edition take idLO passed in URL path
  //   if (idEditionLOActivity) {
  //     return +idEditionLOActivity;
  //   } else {
  //     return (idLO && +idLO) || undefined;
  //   }
  // };

  const handleSubscribe = React.useCallback(
    async (notify: boolean = false) => {
      let listOfUser = Object.keys(selected)
        .map((key: string) => {
          if (key.includes("||")) {
            return +key.split("||")[0];
          }
        })
        .filter((user: any) => {
          return !userAlreadySelected.includes(user);
        });
      try {
        setOpenToast({ s: StatusEnum.LOADING });
        let responsePatch = null;
        responsePatch = await triggerPatchAssociation([
          {
            corporateId: (id && +id) || undefined,
            editionId: (idEdition && +idEdition) || undefined,
            objectId: idEditionLOActivity && +idEditionLOActivity,
            isToj: isToj || false,
            userId: listOfUser,
            mandatory: null,
            isAdmin: true,
          },
        ]);

        setOpenToast({
          s: StatusEnum.OK,
          message: "",
        });
      } catch (e: any) {
        if (isSessionOverlapError(e)) {
          setOpenToast({
            s: StatusEnum.KO,
            message: formatSessionOccupiedError(e, t),
          });
        } else if (
          e.response?.data?.errors?.FATAL?.[0]?.message?.includes(
            MAX_CAPACITY_ERROR_MESSAGE,
          )
        ) {
          setOpenToast({
            s: StatusEnum.KO,
            message: t("max-capacity-reached-error", { ns: "modal" }) || "",
          });
        } else {
          setOpenToast(undefined);
        }
        console.error(e);
      }
    },
    [id, idEdition, idLO, type, selected],
  );

  const getEnrollmentResponse = () => {
    let response = dataTable?.enrollmentResponses || dataTable?.output || [];
    const newList = response.map((item: any) => {
      if (
        !!item.learningObject.blendedParentId &&
        item.learningObject.blendedParentId !== Number(idLO)
      ) {
        return {
          ...item,
          blendedParentId: item.learningObject.blendedParentId,
        };
      } else {
        return item;
      }
    });

    return newList;
  };

  useEffect(() => {
    let alreadySelectedElements = { ...selected };
    let response = dataTable?.enrollmentResponses || dataTable?.output || [];
    response.forEach((item: any) => {
      if (
        item.alreadyEnrolled ||
        (!item.alreadyEnrolled && item?.batchStatus === "IN_PROGRESS")
      ) {
        alreadySelectedElements = {
          ...alreadySelectedElements,
          [getKeysIdTitle(item)]: true,
        };
      }
    });
    setSelected(alreadySelectedElements);
  }, [dataTable]);

  function getKeysIdTitle(originalRow: any) {
    return `${originalRow?.userId}||${originalRow?.enrollId}`;
  }

  const configurationActionForRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.key) {
        case "decompression":
          if (action.icon === "success") {
            return action;
          } else {
            return {
              ...action,
              callBack: (row: any) => {
                getSingleEnroll(row.enrollId);
              },
            };
          }
        default:
          return action;
      }
    });
  }, [actionForRow]);

  //FUNZIONE NECESSARIA PER APPORTARE MODIFICHE AI CAMPI DELLE AZIONI DI RIGA MA IN BASE AL VALORE DEI CAMPI (ROW)
  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = configurationActionForRow && [
      ...configurationActionForRow,
    ];

    const shouldForceSuccessIcon =
      row?.original?.status === "C" &&
      (row?.original?.editionId === undefined ||
        row?.original?.editionId === null);

    configRowNew = configRowNew.map((actionRow: any) => {
      if (actionRow.key === "decompression") {
        var batchStatus = row?.original?.batchStatus;
        //Give priority to absence warning or complete icon
        if (shouldForceSuccessIcon) {
          return {
            ...actionRow,
            icon: "success",
            disabled: true,
            callBack: undefined,
          };
        }
        if (
          row?.original?.participationStatus === "ABSENT" ||
          row?.original?.status === "C"
        ) {
          batchStatus = "COMPLETE";
        }
        if (
          batchStatus === "COMPLETE" &&
          (row?.original?.editionId === undefined ||
            row?.original?.editionId === null)
        ) {
          batchStatus = null;
        }
        switch (batchStatus) {
          case "COMPLETE":
            return {
              ...actionRow,
              icon:
                row?.original?.editionId?.toString() === idEdition
                  ? "success"
                  : row?.original?.editionId?.toString() !== idEdition &&
                      (row?.original?.participationStatus
                        ? row?.original?.participationStatus === "ABSENT"
                        : false)
                    ? "warning"
                    : row?.original?.editionId?.toString() !== idEdition &&
                        row?.original?.status === "C"
                      ? "success"
                      : row?.original?.editionId?.toString() !== idEdition
                        ? "error"
                        : "",
            };
          case "ERROR":
            return { ...actionRow, icon: "error" };
          case "IN_PROGRESS":
            return { ...actionRow, icon: "warning" };
          default:
            if (!!row?.original?.editionId) {
              return { ...actionRow, icon: "error" };
            }
            return { ...actionRow, icon: "" };
        }
      } else {
        return actionRow;
      }
    });

    if (!row?.original?.batchStatus && !shouldForceSuccessIcon) {
      return [
        <CustomRowAction
          key={row.id}
          row={row}
          configurationActionForRow={[
            ...configRowNew.filter(
              (el: any) =>
                ["decompression"].findIndex((type) => type === el.key) < 0,
            ),
          ]}
        />,
      ];
    }

    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
      />,
    ];
  };

  const getSingleEnroll = (enrollId: number) => {
    let response = dataTable?.enrollmentResponses || dataTable?.output || [];
    const enroll = response.filter(
      (item: any) => item.enrollId === enrollId,
    )[0];
    var batchStatus = enroll?.batchStatus;
    if (
      (enroll?.participationStatus === "ABSENT" || enroll?.status === "C") &&
      enroll?.batchStatus === "ERROR"
    ) {
      batchStatus = "COMPLETE";
    }
    if (
      batchStatus === "COMPLETE" &&
      (enroll?.editionId === undefined || enroll?.editionId === null)
    ) {
      batchStatus = null;
    }
    switch (batchStatus) {
      case "ERROR":
        setBatchStatusMessage(enroll?.errorMessage);
        break;

      case "IN_PROGRESS":
        setBatchStatusMessage(
          `${t("association-user.batch-status.pending-message")}`,
        );
        break;

      case "COMPLETE":
        if (
          enroll?.editionId.toString() !== idEdition &&
          (enroll?.participationStatus
            ? enroll?.participationStatus === "ABSENT"
            : false)
        ) {
          setBatchStatusMessage(
            `${t("association-user.batch-status.already-enrolled-absent").replace(":idEdition", enroll?.editionId.toString())}`,
          );
        } else if (enroll?.editionId.toString() !== idEdition) {
          if (enroll?.status === "C") {
            setBatchStatusMessage(
              `${t("association-user.batch-status.already-enrolled-complete").replace(":idEdition", enroll?.editionId.toString())}`,
            );
          } else {
            setBatchStatusMessage(
              `${t("association-user.batch-status.already-enrolled").replace(":idEdition", enroll?.editionId.toString())}`,
            );
          }
        } else {
          setBatchStatusMessage(
            `${t("association-user.batch-status.confirmed-message")}`,
          );
        }
        break;

      default:
        break;
    }
    setOpenBatchStatus(true);
  };

  const handleChangeSelected = (userSelected: any) => {
    //FUNCTION USED TO AVOID BUG ON TABLE LIBRARY TO REMOVE CHECKBOX FOR ELEMENTS DISABLED
    let response = dataTable?.enrollmentResponses || dataTable?.output || [];
    let listOfUserInteractable = response
      .filter((row: any) => {
        return !(
          (!!row?.editionId &&
            row?.participationStatus !== undefined &&
            row?.participationStatus !== null &&
            row?.participationStatus !== "ABSENT") ||
          (row?.alreadyEnrolled && row?.batchStatus === "IN_PROGRESS") ||
          (row?.status === "C" && !row?.editionId)
        );
      })
      .map((user: any) => user.userId);

    let listOfUserNOTInteractable = response
      .filter((row: any) => {
        return (
          (!!row?.editionId && row?.participationStatus !== "ABSENT") ||
          (row?.alreadyEnrolled && row?.batchStatus === "IN_PROGRESS") ||
          (row?.status === "C" && !row?.editionId)
        );
      })
      .map((user: any) => user.userId);

    let listOfUserJustSelected = Object.keys(userSelected).map(
      (key: string) => {
        if (key.includes("||")) {
          return +key.split("||")[0];
        }
      },
    );

    let newSelected: any = { ...selected };
    listOfUserJustSelected.forEach((userId) => {
      let userToAdd: any = Object.keys(userSelected).find((user: any) =>
        user.split("||")[0].includes(userId),
      );
      if (userToAdd) {
        newSelected = { ...newSelected, [userToAdd]: true };
      }
    });
    listOfUserInteractable.forEach((interactable: any) => {
      if (!listOfUserJustSelected.includes(interactable)) {
        let userToDelete: any = Object.keys(newSelected).find((user: any) =>
          user.split("||")[0].includes(interactable),
        );
        if (!!newSelected[userToDelete]) {
          delete newSelected[userToDelete];
        }
      }
    });
    setSelected(newSelected);
    setUserAlreadySelected([
      ...userAlreadySelected,
      ...listOfUserNOTInteractable,
    ]);
  };

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-courses.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${location.state?.row?.title || dataDetail?.business_name} - ${t(
            "association-user.hero-title",
          )}`}
          description={t("association-user.hero-description")}
        />
        <Box
          sx={{
            display: "flex",
            gap: "24px",
            flexDirection: "column",
            [theme.breakpoints.up("sm")]: {
              width: "100%",
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "space-between",
            },
            [theme.breakpoints.up("md")]: {
              width: "auto",
            },
          }}
        >
          <ButtonBackPage customRedirect={-1} />
          <ButtonCst
            id={"save-button"}
            variant={"contained"}
            size={"medium"}
            sx={{}}
            onClick={() => {
              if (notifyMode !== "NONE") {
                setOpenConfirm(true);
              } else {
                handleSubscribe();
              }
            }}
          >
            {t("saveButton", { ns: "form" })}
          </ButtonCst>
        </Box>
        <Box
          sx={{
            mt: "25px",
          }}
        >
          {isLoading ? (
            <FormLoadingStateCst />
          ) : (
            <Table
              id={PagesID["enrollment.edition.user"] + pathname}
              data={getEnrollmentResponse() || []}
              columns={columns || []}
              configurationTopToolbar={actionsTopToolbar}
              renderRowActions={customRenderRowActions}
              totalPages={dataTable?.metadata?.totalElements}
              enableMultiRowSelection={true}
              enableRowSelection={(row: any) => {
                return !(
                  row?.original?.batchStatus === "IN_PROGRESS" ||
                  (!!row?.original?.editionId &&
                    row?.original?.participationStatus !== "ABSENT") ||
                  (row?.original?.status === "C" && !row?.original?.editionId)
                );
              }}
              enableRowActions={true}
              selectedRows={selected}
              onRowSelectionChange={(item: any) => {
                handleChangeSelected(item);
              }}
              getRowId={(originalRow: any) => {
                return getKeysIdTitle(originalRow);
              }}
              numberOfActions={1}
              enableSelectAll={true}
              renamedActionsColumn="Status"
              modAssociationEnabled={true}
              testIdPrefix="user-association-edition"
            />
          )}
        </Box>
      </Container>
      <ModalConfirmsCst
        open={!!openBatchStatus}
        title={t("association-user.batch-status.modal-title")}
        description={batchStatusMessage || ""}
        removeButtons={true}
        onCallBackClosed={() => {
          setOpenBatchStatus(false);
        }}
        onActionConfirmed={() => {}}
      />
      <ModalConfirmsCst
        open={!!openToast}
        title={t("outcome-item", { ns: "modal" })}
        description={t("outcome-item", { ns: "modal" })}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        status={openToast}
      />
      <ModalCst
        open={openConfirm}
        setOpen={setOpenConfirm}
        typeOfModal={"DoubleChoose"}
        title={modalText?.title || ""}
        description={modalText?.message || ""}
        firstChooseLabel={t("association-user.notify-modal.send-notify") || ""}
        secondChooseLabel={
          t("association-user.notify-modal.not-send-notify") || ""
        }
        firstChooseAction={() => {
          setOpenConfirm(false);
          handleSubscribe(true);
        }}
        secondChooseAction={() => {
          setOpenConfirm(false);
        }}
      />
    </AnimationFadeIn>
  );
};

export default AssociationEditionUser;
